/**
 * Inventory (F3, `inventory` capability, church scope): permanent items
 * (instruments, chairs) and consumables (cleaning supplies) with category,
 * quantity, venue location, status, and owner; LOW-STOCK alert;
 * movimientos (entrada/salida/traslado/ajuste) como LIBRO auditado por
 * activity-core (client writes movement and new item state
 * in same local transaction — `applyMovement` is shared pure logic).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import {
  applyMovement,
  buildInventoryDataset,
  exportDataset,
  getChurchesQuery,
  getInventoryItemsQuery,
  getInventoryMovementsQuery,
  getPeopleQuery,
  getVenuesQuery,
  type InventoryMovementKind,
  inventorySummary,
  isLowStock,
  patchRowVerified,
  queryKeys,
} from "@agape/shared";
import {
  type ChurchRecord,
  type InventoryItemRecord,
  inventory_item as inventoryItemTable,
  type InventoryMovementRecord,
  inventory_movement as inventoryMovementTable,
  type PersonRecord,
  type VenueRecord,
} from "@agape/powersync";
import {
  AlertTriangle,
  ArrowLeftRight,
  Boxes,
  Download,
  Package,
  Plus,
  Trash2,
} from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog } from "./form";
import { formatWhen, newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

type Translate = ReturnType<typeof useI18n>["t"];

function movementKindLabel(t: Translate, kind: string | null): string {
  switch (kind) {
    case "in": {
      return t(msg`Stock in`);
    }
    case "out": {
      return t(msg`Stock out`);
    }
    case "transfer": {
      return t(msg`Transfer`);
    }
    default: {
      return t(msg`Recount`);
    }
  }
}

export function InventoryPage() {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const items = useDomainQuery<InventoryItemRecord>(
    (uid) => queryKeys.domain.inventoryItems(uid),
    (db) => getInventoryItemsQuery(db),
  );
  const movements = useDomainQuery<InventoryMovementRecord>(
    (uid) => queryKeys.domain.inventoryMovements(uid),
    (db) => getInventoryMovementsQuery(db),
  );
  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const venues = useDomainQuery<VenueRecord>(
    (uid) => queryKeys.domain.venues(uid),
    (db) => getVenuesQuery(db),
  );
  const people = useDomainQuery<PersonRecord>(
    (uid) => queryKeys.domain.people(uid),
    (db) => getPeopleQuery(db),
  );

  const inventoryChurches = createMemo(() =>
    (churches.data ?? []).filter((c) => grants.can([c.id], "inventory", "read")),
  );
  const [churchId, setChurchId] = createSignal("");
  const activeChurch = () =>
    churchId() || inventoryChurches()[0]?.id || (items.data ?? [])[0]?.church_id || "";

  const churchItems = createMemo(() =>
    (items.data ?? []).filter((i) => i.church_id === activeChurch()),
  );
  const churchVenues = createMemo(() =>
    (venues.data ?? []).filter((v) => v.church_id === activeChurch()),
  );
  const churchPeople = createMemo(() =>
    (people.data ?? []).filter((p) => p.church_id === activeChurch()),
  );
  const venueName = (id: string | null) => (venues.data ?? []).find((v) => v.id === id)?.name ?? "";

  const summary = createMemo(() =>
    inventorySummary(
      churchItems().map((i) => ({
        id: i.id,
        name: i.name,
        kind: i.kind,
        category: i.category,
        quantity: i.quantity,
        min_quantity: i.min_quantity,
        venue_id: i.venue_id,
      })),
    ),
  );

  const canWrite = () => grants.can([activeChurch()], "inventory", "write");

  // ── Item create/edit ──────────────────────────────────────────────────────
  const [open, setOpen] = createSignal(false);
  const [editing, setEditing] = createSignal<string | null>(null);
  const [name, setName] = createSignal("");
  const [kind, setKind] = createSignal("permanent");
  const [category, setCategory] = createSignal("");
  const [quantity, setQuantity] = createSignal("1");
  const [minQuantity, setMinQuantity] = createSignal("0");
  const [venueId, setVenueId] = createSignal("");
  const [condition, setCondition] = createSignal("");
  const [responsibleId, setResponsibleId] = createSignal("");
  const [notes, setNotes] = createSignal("");

  const openCreate = () => {
    setEditing(null);
    setName("");
    setKind("permanent");
    setCategory("");
    setQuantity("1");
    setMinQuantity("0");
    setVenueId("");
    setCondition("");
    setResponsibleId("");
    setNotes("");
    setOpen(true);
  };

  const openEdit = (item: InventoryItemRecord) => {
    setEditing(item.id);
    setName(item.name ?? "");
    setKind(item.kind ?? "permanent");
    setCategory(item.category ?? "");
    setQuantity(String(item.quantity ?? 0));
    setMinQuantity(String(item.min_quantity ?? 0));
    setVenueId(item.venue_id ?? "");
    setCondition(item.condition ?? "");
    setResponsibleId(item.responsible_person_id ?? "");
    setNotes(item.notes ?? "");
    setOpen(true);
  };

  const saveItem = async () => {
    const qty = Number(quantity());
    const minQty = Number(minQuantity());
    if (
      !name().trim() ||
      !Number.isInteger(qty) ||
      qty < 0 ||
      !Number.isInteger(minQty) ||
      minQty < 0
    ) {
      showToast(t(msg`Enter a name and non-negative integer quantities`), "error");
      return;
    }
    const values = {
      name: name().trim(),
      kind: kind(),
      category: category().trim() || null,
      quantity: qty,
      min_quantity: minQty,
      venue_id: venueId() || null,
      condition: condition().trim() || null,
      responsible_person_id: responsibleId() || null,
      notes: notes().trim() || null,
      updated_at: nowIso(),
    };
    const id = editing();
    await (id
      ? patchRowVerified(write(), inventoryItemTable, inventoryItemTable.id, id, values)
      : write()
          .insert(inventoryItemTable)
          .values({ ...values, id: newId(), church_id: activeChurch(), created_at: nowIso() }));
    setOpen(false);
  };

  const trashItem = async (id: string) => {
    try {
      await patchRowVerified(write(), inventoryItemTable, inventoryItemTable.id, id, {
        deleted_at: nowIso(),
      });
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };

  // ── Movement (in/out/transfer/adjust) ─────────────────────────────────────
  const [movementItem, setMovementItem] = createSignal<InventoryItemRecord | null>(null);
  const [movementKind, setMovementKind] = createSignal<InventoryMovementKind>("in");
  const [movementQty, setMovementQty] = createSignal("1");
  const [movementVenue, setMovementVenue] = createSignal("");
  const [movementNote, setMovementNote] = createSignal("");

  const recordMovement = async () => {
    const item = movementItem();
    if (!item) {
      return;
    }
    const qty = Number(movementQty());
    let next: { quantity: number; venueId: string | null };
    try {
      next = applyMovement(
        { quantity: item.quantity, venue_id: item.venue_id },
        { kind: movementKind(), quantity: qty, toVenueId: movementVenue() || null },
      );
    } catch {
      showToast(t(msg`Impossible movement for the current stock`), "error");
      return;
    }
    // Ledger + item state (offline-safe: both mutations land in the
    // local CRUD queue and the server validates each with `inventory:write`;
    // the movement is audited by activity-core). They are NOT in one
    // transaction — if the stock UPDATE matched no rows (the view
    // window), the already-recorded movement would be orphaned from a real
    // stock change: `patchRowVerified` re-reads/retries and, if it
    // truly does not land, throws instead of letting it vanish silently.
    try {
      await write()
        .insert(inventoryMovementTable)
        .values({
          id: newId(),
          item_id: item.id,
          kind: movementKind(),
          quantity: qty,
          from_venue_id: movementKind() === "transfer" ? item.venue_id : null,
          to_venue_id: movementKind() === "transfer" ? movementVenue() || null : null,
          note: movementNote().trim() || null,
          occurred_at: nowIso(),
          created_at: nowIso(),
          updated_at: nowIso(),
        });
      await patchRowVerified(write(), inventoryItemTable, inventoryItemTable.id, item.id, {
        quantity: next.quantity,
        venue_id: next.venueId,
      });
      setMovementItem(null);
      setMovementQty("1");
      setMovementNote("");
    } catch {
      showToast(
        t(msg`The movement was recorded but the stock update didn't save — check the item`),
        "error",
      );
    }
  };

  const delivery = useKernelService(EXPORT_DELIVERY);

  const exportCsv = () => {
    const target = delivery();
    if (!target) {
      return;
    }
    const church = inventoryChurches().find((c) => c.id === activeChurch());
    const dataset = buildInventoryDataset(
      churchItems().map((i) => ({
        id: i.id,
        name: i.name,
        kind: i.kind,
        category: i.category,
        quantity: i.quantity,
        min_quantity: i.min_quantity,
        venue_id: i.venue_id,
        condition: i.condition,
        responsible_name: i.responsible_name,
        venueName: venueName(i.venue_id),
        notes: i.notes,
      })),
      { subtitle: church?.name ?? undefined },
    );
    void exportDataset(dataset, "csv", target);
  };

  return (
    <div class="space-y-4" data-testid="inventory-page">
      <PageSubHeader title={t(msg`Inventory`)} />
      <Show
        when={inventoryChurches().length > 0 || (items.data ?? []).length > 0}
        fallback={
          <p data-testid="inventory-empty" class="text-muted-foreground">
            {t(
              msg`No inventory synced to this account. Ask a manager for the inventory capability.`,
            )}
          </p>
        }
      >
        <div class="flex flex-wrap items-end gap-3">
          <Show when={inventoryChurches().length > 0}>
            <div class="min-w-44">
              <Select
                options={inventoryChurches().map((c) => ({ value: c.id, label: c.name ?? c.id }))}
                value={activeChurch()}
                onChange={(v) => setChurchId(v)}
                aria-label={t(msg`Church`)}
                data-testid="inventory-church-select"
              />
            </div>
          </Show>
          <div class="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              data-testid="inventory-export-csv"
            >
              <Download size={14} /> {t(msg`CSV`)}
            </Button>
            <Show when={canWrite()}>
              <Button size="sm" onClick={openCreate} data-testid="inventory-add">
                <Plus size={14} /> {t(msg`Item`)}
              </Button>
            </Show>
          </div>
        </div>

        {/* Aviso de bajo-stock */}
        <Show when={summary().lowStock.length > 0}>
          <Card class="border-destructive/40">
            <CardContent class="flex items-start gap-3 py-3" data-testid="inventory-low-stock">
              <AlertTriangle size={18} class="mt-0.5 shrink-0 text-destructive" />
              <div class="min-w-0">
                <p class="font-medium text-destructive">{t(msg`Low stock`)}</p>
                <p class="text-sm text-muted-foreground">
                  <For each={summary().lowStock}>
                    {(low, i) => (
                      <>
                        {i() > 0 ? " · " : ""}
                        <span data-testid="inventory-low-stock-item">
                          {low.name} ({low.quantity ?? 0}/{low.min_quantity ?? 0})
                        </span>
                      </>
                    )}
                  </For>
                </p>
              </div>
            </CardContent>
          </Card>
        </Show>

        {/* Ítems */}
        <Show
          when={churchItems().length > 0}
          fallback={
            <p class="text-muted-foreground" data-testid="inventory-list-empty">
              {t(msg`No items yet. Add instruments, chairs or cleaning supplies.`)}
            </p>
          }
        >
          <div class="space-y-2" data-testid="inventory-list">
            <For each={churchItems()}>
              {(item) => (
                <Card
                  data-testid="inventory-item"
                  data-item-name={item.name ?? ""}
                  data-low-stock={
                    isLowStock({ quantity: item.quantity, min_quantity: item.min_quantity })
                      ? "true"
                      : "false"
                  }
                >
                  <CardContent class="flex items-center gap-3 py-3">
                    <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                      <Show when={item.kind === "consumable"} fallback={<Boxes size={16} />}>
                        <Package size={16} />
                      </Show>
                    </span>
                    <button
                      type="button"
                      class="min-w-0 flex-1 text-left"
                      onClick={() => canWrite() && openEdit(item)}
                      data-testid="inventory-item-edit"
                    >
                      <p class="truncate font-medium">
                        {item.name}
                        <Show
                          when={isLowStock({
                            quantity: item.quantity,
                            min_quantity: item.min_quantity,
                          })}
                        >
                          <span class="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive">
                            {t(msg`Low stock`)}
                          </span>
                        </Show>
                      </p>
                      <p class="truncate text-sm text-muted-foreground">
                        {[
                          item.kind === "consumable" ? t(msg`Consumable`) : t(msg`Permanent`),
                          item.category,
                          venueName(item.venue_id),
                          item.condition,
                          item.responsible_name,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </button>
                    <p
                      class="shrink-0 font-semibold tabular-nums"
                      data-testid="inventory-item-quantity"
                    >
                      {item.quantity ?? 0}
                    </p>
                    <Show when={canWrite()}>
                      <Button
                        variant="outline"
                        size="icon"
                        class="size-8"
                        aria-label={t(msg`Record movement`)}
                        data-testid="inventory-item-move"
                        onClick={() => {
                          setMovementItem(item);
                          setMovementKind("in");
                          setMovementQty("1");
                          setMovementVenue("");
                        }}
                      >
                        <ArrowLeftRight size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        class="size-8 text-muted-foreground hover:text-destructive"
                        aria-label={t(msg`Move to trash`)}
                        data-testid="inventory-item-trash"
                        onClick={() => void trashItem(item.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </Show>
                  </CardContent>
                </Card>
              )}
            </For>
          </div>
        </Show>

        {/* Libro de movimientos */}
        <Show when={(movements.data ?? []).some((m) => m.church_id === activeChurch())}>
          <Card>
            <CardContent class="space-y-1 py-3" data-testid="inventory-movements">
              <p class="text-xs font-bold uppercase tracking-wider opacity-60">
                {t(msg`Movement log`)}
              </p>
              <For
                each={(movements.data ?? [])
                  .filter((m) => m.church_id === activeChurch())
                  .slice(0, 20)}
              >
                {(m) => {
                  const item = (items.data ?? []).find((i) => i.id === m.item_id);
                  return (
                    <div
                      class="flex items-center justify-between gap-2 text-sm"
                      data-testid="inventory-movement"
                    >
                      <span class="min-w-0 truncate">
                        {movementKindLabel(t, m.kind)} · {item?.name ?? m.item_id}
                        <Show when={m.note}> — {m.note}</Show>
                      </span>
                      <span class="shrink-0 tabular-nums text-muted-foreground">
                        {m.kind === "out" ? "−" : m.kind === "in" ? "+" : ""}
                        {m.quantity ?? 0}
                        {" · "}
                        {m.occurred_at ? formatWhen(Date.parse(m.occurred_at)) : ""}
                      </span>
                    </div>
                  );
                }}
              </For>
            </CardContent>
          </Card>
        </Show>
      </Show>

      {/* Diálogo: ítem */}
      <FormDialog
        open={open()}
        onOpenChange={setOpen}
        title={editing() ? t(msg`Edit item`) : t(msg`New item`)}
        icon={<Boxes size={20} />}
        submitTestId="inventory-submit"
        onSubmit={saveItem}
      >
        <Field
          id="inv-name"
          label={t(msg`Name`)}
          value={name()}
          onInput={setName}
          placeholder={t(msg`Chairs, guitar, bleach…`)}
          testId="inventory-name-input"
        />
        <div class="grid grid-cols-2 gap-3">
          <Field id="inv-kind" label={t(msg`Kind`)}>
            <Select
              options={[
                { value: "permanent", label: t(msg`Permanent`) },
                { value: "consumable", label: t(msg`Consumable`) },
              ]}
              value={kind()}
              onChange={setKind}
              aria-label={t(msg`Kind`)}
              data-testid="inventory-kind-select"
            />
          </Field>
          <Field
            id="inv-category"
            label={t(msg`Category`)}
            value={category()}
            onInput={setCategory}
            placeholder={t(msg`Furniture, sound, cleaning…`)}
            testId="inventory-category-input"
          />
          <Field
            id="inv-quantity"
            label={t(msg`Quantity`)}
            type="number"
            value={quantity()}
            onInput={setQuantity}
            testId="inventory-quantity-input"
          />
          <Field
            id="inv-min"
            label={t(msg`Low-stock threshold`)}
            type="number"
            value={minQuantity()}
            onInput={setMinQuantity}
            testId="inventory-min-input"
          />
          <Field id="inv-venue" label={t(msg`Location (venue)`)}>
            <Select
              options={[
                { value: "", label: t(msg`No location`) },
                ...churchVenues().map((v) => ({ value: v.id, label: v.name ?? v.id })),
              ]}
              value={venueId()}
              onChange={setVenueId}
              aria-label={t(msg`Location (venue)`)}
              data-testid="inventory-venue-select"
            />
          </Field>
          <Field id="inv-condition" label={t(msg`Condition`)}>
            <Select
              options={[
                { value: "", label: t(msg`Unspecified`) },
                { value: "good", label: t(msg`Good`) },
                { value: "fair", label: t(msg`Fair`) },
                { value: "poor", label: t(msg`Poor`) },
                { value: "broken", label: t(msg`Broken`) },
              ]}
              value={condition()}
              onChange={setCondition}
              aria-label={t(msg`Condition`)}
              data-testid="inventory-condition-select"
            />
          </Field>
        </div>
        <Field id="inv-responsible" label={t(msg`Responsible`)}>
          <Select
            options={[
              { value: "", label: t(msg`Nobody`) },
              ...churchPeople().map((p) => ({ value: p.id, label: p.name ?? p.id })),
            ]}
            value={responsibleId()}
            onChange={setResponsibleId}
            aria-label={t(msg`Responsible`)}
            data-testid="inventory-responsible-select"
          />
        </Field>
        <Field
          id="inv-notes"
          label={t(msg`Notes`)}
          value={notes()}
          onInput={setNotes}
          testId="inventory-notes-input"
        />
      </FormDialog>

      {/* Diálogo: movimiento */}
      <FormDialog
        open={movementItem() !== null}
        onOpenChange={(o) => !o && setMovementItem(null)}
        title={t(msg`Record movement`)}
        icon={<ArrowLeftRight size={20} />}
        submitTestId="movement-submit"
        onSubmit={recordMovement}
      >
        <p class="text-sm text-muted-foreground">
          {movementItem()?.name} — {t(msg`current stock:`)} {movementItem()?.quantity ?? 0}
        </p>
        <div class="grid grid-cols-2 gap-3">
          <Field id="mov-kind" label={t(msg`Movement`)}>
            <Select
              options={[
                { value: "in", label: t(msg`Stock in`) },
                { value: "out", label: t(msg`Stock out`) },
                { value: "transfer", label: t(msg`Transfer`) },
                { value: "adjust", label: t(msg`Recount`) },
              ]}
              value={movementKind()}
              onChange={(v) =>
                setMovementKind(v === "out" || v === "transfer" || v === "adjust" ? v : "in")
              }
              aria-label={t(msg`Movement`)}
              data-testid="movement-kind-select"
            />
          </Field>
          <Field
            id="mov-qty"
            label={movementKind() === "adjust" ? t(msg`New total`) : t(msg`Units`)}
            type="number"
            value={movementQty()}
            onInput={setMovementQty}
            testId="movement-quantity-input"
          />
        </div>
        <Show when={movementKind() === "transfer"}>
          <Field id="mov-venue" label={t(msg`Destination venue`)}>
            <Select
              options={[
                { value: "", label: t(msg`No location`) },
                ...churchVenues().map((v) => ({ value: v.id, label: v.name ?? v.id })),
              ]}
              value={movementVenue()}
              onChange={setMovementVenue}
              aria-label={t(msg`Destination venue`)}
              data-testid="movement-venue-select"
            />
          </Field>
        </Show>
        <Field
          id="mov-note"
          label={t(msg`Note`)}
          value={movementNote()}
          onInput={setMovementNote}
          testId="movement-note-input"
        />
      </FormDialog>
    </div>
  );
}
